import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@components/Badge/Badge";
import { Button } from "@components/Button";
import { ExclamationCircle20Icon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { useTranslationArray } from "@hooks/useTranslationArray";
import { type IUseModalProps } from "@store/modal/modal";
import { useOpenJointBillServiceCurrentPeriodMutation } from "@services/jointBillServices/mutations";

interface IOpenPeriodModalProps extends IUseModalProps {
  jointBillServiceId: number;
  periodId: number;
}

export const OpenPeriodModal: React.FC<IOpenPeriodModalProps> = (props) => {
  const { jointBillServiceId, periodId, ...rest } = props;
  const { t } = useTranslation();
  const modal = useModal(rest);

  const { mutateAsync, isPending } =
    useOpenJointBillServiceCurrentPeriodMutation();

  const onAgree = async () => {
    const response = await mutateAsync({
      jointBillServiceId: jointBillServiceId,
      periodId: periodId,
    }).catch(() => {
      toast.error(t("toast.error"));
    });

    if (response) {
      toast.success(t("openPeriodModal.toast.success"));
      modal.close();
    }
  };

  const descriptionItems = useTranslationArray("openPeriodModal.items");

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: ExclamationCircle20Icon, color: "red" }}
      title={t("openPeriodModal.title")}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("openPeriodModal.buttons.cancel")}
          </Button>
          <Button className="w-full" onClick={onAgree} isLoading={isPending}>
            {t("openPeriodModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        {descriptionItems?.map((description, index) => (
          <div className="flex items-center" key={index}>
            <Badge
              icon={ExclamationCircle20Icon}
              variant="bubble"
              color="red"
              size="large"
              className="mr-4 p-2"
            />
            <p className="text-left text-sm">{description}</p>
          </div>
        ))}
      </div>
    </Modal>
  );
};
